import AddIcon from "@mui/icons-material/Add";
import React, { ChangeEvent, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectSupportedFileTypes } from "../../app/slices/AssetsSlice";
import { useNewAssetUpload } from "../../hooks/UploadNewAssetHook";
import "./FileDropzone.css";

interface FileDropzoneProps {
  /**
   * "row" is the list view's dashed upload row: same behaviour, laid out
   * horizontally in a compact strip instead of the tall card.
   */
  variant?: "card" | "row";
}

/**
 * Click-to-browse upload affordance leading the results.
 *
 * It no longer handles drags: the whole results region does (`PiDropRegion`),
 * so a file can be dropped at any scroll position rather than only onto this
 * one strip. Two drag systems over the same area is how you end up with a
 * zone stuck in its hover state.
 */

export default function FileDropzone({ variant = "card" }: FileDropzoneProps) {
  const { t } = useTranslation();
  const [handleFiles, canUpload] = useNewAssetUpload();
  const inputRef = useRef<HTMLInputElement>(null);
  const supportedFileTypes = useSelector(selectSupportedFileTypes);

  const handleFileInput = function (e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files?.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const openFileExplorer = () => {
    // Nothing to upload into: opening the picker here would take a file and
    // silently discard it, which is what this used to do.
    if (!canUpload) return;
    inputRef.current?.click();
  };

  const classname = [
    "filedropzone-root",
    variant === "row" && "filedropzone-row",
    !canUpload && "filedropzone-disabled",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={classname}
      onClick={openFileExplorer}
      id="filedropzone"
      data-testid="file-explorer-dropzone"
      data-disabled={!canUpload ? "true" : undefined}
      aria-disabled={!canUpload}
    >
      <div className="filedropzone-content">
        <div>
          <div className="filedropzone-icon">
            <AddIcon />
          </div>
          <div className="filedropzone-title">
            {canUpload
              ? t("dropYourFilesHere")
              : t("file_explorer_ui.upload_needs_folder_title")}
          </div>
          <div className="filedropzone-subtitle">
            {canUpload ? (
              <>
                <span>{t("browseFiles")}</span> {t("fromYourComputer")}
              </>
            ) : (
              t("file_explorer_ui.upload_needs_folder_body")
            )}
          </div>
        </div>
      </div>
      {canUpload && (
        <input
          className="filedropzone-hide-input"
          ref={inputRef}
          type="file"
          onChange={handleFileInput}
          accept={supportedFileTypes.toString()}
          multiple
          data-testid="file-explorer-dropzone-input"
        />
      )}
    </div>
  );
}
